// FLAGS: none
// Generic debounced single-select autocomplete pill used by the session filters.
import React, { useState } from "react";
import { Autocomplete, TextField, InputAdornment } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { colors } from "@src/theme/colors";
import { useDebouncedCallback } from "use-debounce";

const StyledAutocomplete = styled(Autocomplete)({
  "&.MuiAutocomplete-root .MuiInputBase-root": {
    padding: "8px 12px",
    borderRadius: "9999px",
  },
  "&.MuiAutocomplete-root .MuiInputBase-root *": {
    color: colors.primaryDarkBlue,
  },
  "&.MuiAutocomplete-hasClearIcon .MuiInputBase-root": {
    backgroundColor: colors.primaryDarkBlue,
  },
  "&.MuiAutocomplete-hasClearIcon .MuiInputBase-root *": {
    color: "#fff",
  },
  "&.MuiAutocomplete-root .MuiInputAdornment-root": {
    margin: "0 4px 0 8px!important",
  },
});

interface DropDownFilterProps<T> {
  value: T | null;
  onChange: (employer: T | null) => void;
  onSearchChanged: (value: string) => void;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue: (option: T, value: T) => boolean;
  options?: T[];
  loading?: boolean;
  "data-testid"?: string;
  noRecordFoundText?: string;
  placeholder?: string;
  inputIcon?: React.ReactNode;
}

const DropdownFilter = <T,>({
  value,
  onChange,
  onSearchChanged,
  getOptionLabel,
  isOptionEqualToValue,
  noRecordFoundText,
  placeholder,
  options = [],
  loading = false,
  inputIcon,
  "data-testid": dataTestId = "dropdown-filter",
}: React.PropsWithChildren<DropDownFilterProps<T>>) => {
  const [isOpen, setIsOpen] = useState(false);
  const debounced = useDebouncedCallback((inputValue: string) => {
    onSearchChanged(inputValue);
  }, 500);

  return (
    <StyledAutocomplete
      blurOnSelect
      disablePortal
      data-testid={dataTestId}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      className="w-60"
      loading={loading}
      options={options}
      getOptionLabel={getOptionLabel as (option: unknown) => string}
      isOptionEqualToValue={isOptionEqualToValue}
      onInputChange={(_, newInputValue) => {
        debounced(newInputValue);
      }}
      noOptionsText={noRecordFoundText}
      filterOptions={(x) => x}
      value={value}
      onChange={(_, newValue) => {
        onChange(newValue as T | null);
      }}
      popupIcon={<ExpandMore />}
      renderInput={(params) => {
        return (
          <TextField
            variant="filled"
            {...params}
            placeholder={placeholder}
            fullWidth
            size="small"
            InputProps={{
              ...params.InputProps,
              className: "!sub1",
              disableUnderline: true,
              startAdornment: inputIcon ? (
                <InputAdornment position="start">{inputIcon}</InputAdornment>
              ) : undefined,
            }}
          />
        );
      }}
    />
  );
};

export default DropdownFilter;
